import { Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { decodeUrlConfig } from '../hooks/use-url-config';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { ChatMessageItem } from './ChatMessageItem';
import { ChatTypingLoader } from './ChatTypingLoader';
import { useLocation } from 'react-router-dom';

export type Message = {
  id: string;
  sender: 'Inbound' | 'Outbound';
  text: string;
  time: string;
};

const PageWrapper = styled(Box)(() => ({
  backgroundColor: '#ffffff',
  height: '100vh',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ChatWrapper = styled(Box)(() => ({
  backgroundColor: '#f5f5f5',
  width: '100%',
  maxWidth: '1100px',
  height: 'calc(100vh - 40px)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  boxShadow: '0 0 10px rgba(0,0,0,0.05)',
}));

const ChatContainer = styled(Card)(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: '20px',
  borderRadius: '0',
  width: '100%',
  backgroundColor: 'inherit',
  overflowY: 'auto',
  height: 'calc(100vh - 180px)',
}));

const ChatContent = styled(CardContent)(() => ({
  gap: '30px',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '1100px',
  marginLeft: 'auto',
  marginRight: 'auto',
  '&:last-child': { paddingBottom: '0px' },
}));

const ChatConversation = () => {
  const location = useLocation();
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    'https://api.aktify.io/conversation/v1/demochat/respond';

  const urlConfig = decodeUrlConfig(location.pathname);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: urlConfig.first_message ?? 'Hi! Welcome to Pest Control Co. How can we help?',
      sender: 'Outbound',
      time: moment().toISOString(),
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLInputElement | null>(null);

  const companyName = urlConfig.company_name;

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage: Message = {
      id: (messages.length + 1).toString(),
      text: newMessage,
      sender: 'Inbound',
      time: moment().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setNewMessage('');
    setIsBotTyping(true);

    try {
      const res = await fetch(`${API_BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Special-Header': 'expected-value',
        },
        body: JSON.stringify({
          time_cust_sent: new Date().toISOString(),
          msg_body: newMessage,
          timezone: 'America/Denver',
          agent_id: urlConfig.agent_id,
          events: [],
        }),
      });

      const data = await res.json();
      const botMessage: Message = {
        id: (messages.length + 2).toString(),
        text: data.response || 'Sorry, no response received.',
        sender: 'Outbound',
        time: moment().toISOString(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsBotTyping(false);
    }
  };

  return (
    <PageWrapper>
      <ChatWrapper>
        <Typography variant="h6" textAlign="center" sx={{ mt: 2 }}>
          {companyName}
        </Typography>

        <Stack direction="column" sx={{ flexGrow: 1 }} padding={1}>
          <ChatContainer>
            <ChatContent>
              {messages.map((message, index) => (
                <ChatMessageItem key={index} message={message} />
              ))}
              {isBotTyping && (
                <Box mt={1}>
                  <ChatTypingLoader />
                </Box>
              )}
              <div ref={chatEndRef} />
            </ChatContent>
          </ChatContainer>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              maxWidth: '1070px',
              width: '100%',
            }}
          >
            <TextField
              inputRef={messageInputRef}
              variant="outlined"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyUp={(e) => e.key === 'Enter' && sendMessage()}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={sendMessage}
              sx={{
                borderRadius: '8px',
                minWidth: 80,
                height: '56px',
                textTransform: 'none',
              }}
            >
              Send
            </Button>
          </Stack>
        </Stack>
      </ChatWrapper>
    </PageWrapper>
  );
};

export default ChatConversation;
